// Rollover cycle history — compact side card.
//
// Reads terminal Phantom cycles from usePhantomCycleHistory(). Empty state
// falls back to a single "Chain seeded" row when no terminal cycles exist.
//
// Visuals: Football Products (FpKit) Kalshi-clean — inline FpKit tokens,
// hairline borders, mono uppercase labels, no gradients.

import { Icon } from "../../components/icons/Icon.tsx";
import type { PhantomCycleRow, PhantomSelection } from "../../phantom/usePhantomCycle.ts";

export interface RolloverCycleHistoryProps {
  readonly cycles: readonly PhantomCycleRow[];
  readonly chainCreatedAt: string | null;
  readonly chainShortId: string | null;
}

function firstSelection(selections: PhantomCycleRow["selections"]): string {
  if (Array.isArray(selections) && selections.length > 0) {
    const s = selections[0] as PhantomSelection;
    if (s?.phantom_pick) return s.phantom_pick;
    if (s?.home_team && s?.away_team) return `${s.home_team} v ${s.away_team}`;
  }
  return "—";
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function resCls(status: string): { res: string; color: string } {
  switch (status) {
    case "phantom_won":
      return { res: "WIN", color: "var(--fp-won)" };
    case "phantom_busted":
      return { res: "LOSS", color: "var(--fp-lost)" };
    case "expired":
      return { res: "EXPIRED", color: "var(--text-tertiary)" };
    default:
      return { res: status.toUpperCase(), color: "var(--text-tertiary)" };
  }
}

const SUB_STYLE: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  color: "var(--text-quaternary)",
  letterSpacing: "0.02em",
  marginTop: 2,
};

const WK_STYLE: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "var(--text-tertiary)",
  whiteSpace: "nowrap",
  paddingTop: 1,
};

export function RolloverCycleHistory({
  cycles,
  chainCreatedAt,
  chainShortId,
}: RolloverCycleHistoryProps): JSX.Element {
  return (
    <div
      style={{
        background: "var(--bg-elev)",
        border: "1px solid var(--border)",
        borderRadius: 10,
        padding: 14,
      }}
    >
      {/* Card head */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          paddingBottom: 12,
          marginBottom: 10,
          borderBottom: "1px solid var(--border-faint)",
        }}
      >
        <span style={{ color: "var(--fp-accent-text)", display: "inline-flex" }}>
          <Icon name="audit" size={14} />
        </span>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: "0.14em",
              color: "var(--fp-accent-text)",
            }}
          >
            Ledger
          </div>
          <h3
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 18,
              lineHeight: 1,
              margin: 0,
              color: "var(--text)",
              letterSpacing: "0.01em",
            }}
          >
            Cycle history
          </h3>
        </div>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "var(--text-tertiary)",
            whiteSpace: "nowrap",
          }}
        >
          View audit trail →
        </span>
      </div>

      {/* History table */}
      <div style={{ display: "flex", flexDirection: "column" }}>
        {cycles.map((c) => {
          const { res, color } = resCls(c.status);
          const profit =
            c.payout !== null && c.stake_amount !== null ? c.payout - c.stake_amount : null;
          const profitStr =
            profit !== null
              ? `${profit >= 0 ? "+" : ""}GH₵${Math.round(profit).toLocaleString()}`
              : "";
          return (
            <div
              key={c.id}
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                gap: 10,
                alignItems: "flex-start",
                padding: "9px 0",
                borderBottom: "1px solid var(--border-faint)",
              }}
            >
              <div style={WK_STYLE}>Wk {c.week_number}</div>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: "var(--text)",
                    lineHeight: 1.3,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {firstSelection(c.selections)}
                </div>
                <div style={SUB_STYLE}>
                  {c.stake_amount !== null
                    ? `GH₵${Math.round(c.stake_amount).toLocaleString()}`
                    : "—"}
                  {c.payout !== null
                    ? ` → GH₵${Math.round(c.payout).toLocaleString()}`
                    : ""}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    color,
                  }}
                >
                  {res}
                </div>
                <div style={{ ...SUB_STYLE, color }}>{profitStr}</div>
              </div>
            </div>
          );
        })}

        {/* Chain seeded — open row */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr auto",
            gap: 10,
            alignItems: "flex-start",
            padding: "9px 0 0",
          }}
        >
          <div style={WK_STYLE}>—</div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                color: "var(--text)",
                lineHeight: 1.3,
              }}
            >
              Chain seeded
            </div>
            <div style={SUB_STYLE}>
              {chainCreatedAt ? shortDate(chainCreatedAt) : "—"}
              {chainShortId ? ` · plan #${chainShortId}` : ""}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.06em",
                color: "var(--fp-accent-text)",
              }}
            >
              OPEN
            </div>
            <div style={SUB_STYLE} />
          </div>
        </div>
      </div>
    </div>
  );
}
